import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import { ChevronLeft, MapPin, Search, Navigation, AlertCircle } from 'lucide-react';
import DistrictSelect from '../utils/DistrictSelect';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

function MapSelection({  userId, setActiveComponent }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [address, setAddress] = useState('');
  const [district, setDistrict] = useState('');
  const currentMarkerRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showInitialAlert, setShowInitialAlert] = useState(true);

  useEffect(() => {
    if (mapContainerRef.current && window.google) {
      let mapCenter = { lat: -12.060235303763926, lng: -77.03711992646711 };
      let zoomLevel = 12;

      mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
        center: mapCenter,
        zoom: zoomLevel,
        disableDefaultUI: true,
      });

      mapRef.current.addListener('click', (e) => {
        const { latLng } = e;
        const location = {
          lat: latLng.lat(),
          lng: latLng.lng(),
        };
        addMarker(location);
        setSelectedLocation(location);
        setShowInitialAlert(false);
      });
    }

    return () => {
      if (mapRef.current) {
        mapRef.current = null;
      }
    };
  }, [userId]);

  function addMarker(location) {
    if (currentMarkerRef.current) {
      currentMarkerRef.current.setMap(null);
    }

    currentMarkerRef.current = new window.google.maps.Marker({
      position: location,
      map: mapRef.current,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: '#B1336E',
        fillOpacity: 1,
        scale: 10,
        strokeColor: 'white',
        strokeWeight: 2,
      },
    });
  }

  async function fetchAutocompleteSuggestions(input) {
    if (input.trim().length === 0) {
      setSearchResults([]);
      return;
    }

    const autocompleteService = new window.google.maps.places.AutocompleteService();
    autocompleteService.getPlacePredictions(
      {
        input,
        componentRestrictions: { country: 'pe' },
        location: new window.google.maps.LatLng(-12.0464, -77.0428),
        radius: 50000,
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSearchResults(predictions);
        } else {
          setSearchResults([]);
        }
      }
    );
  }

  function handleSearchChange(event) {
    const input = event.target.value;
    setSearchQuery(input);
    fetchAutocompleteSuggestions(input);
  }

  function handleResultClick(result) {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: result.place_id }, (results, status) => {
      if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
        const location = {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        };
        setSelectedLocation(location);
        addMarker(location);
        mapRef.current.panTo(location);
        mapRef.current.setZoom(17);
        setShowInitialAlert(false);
      }
    });
    setSearchQuery(result.description);
    setSearchResults([]);
  }

  function handleGetCurrentLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const location = { lat: latitude, lng: longitude };
          setSelectedLocation(location);
          addMarker(location);
          mapRef.current.panTo(location);
          mapRef.current.setZoom(16);
          setShowInitialAlert(false);
        },
        (error) => {
          console.error('Error getting current location:', error);
          setErrorMessage('No se pudo obtener tu ubicación actual');
        }
      );
    } else {
      setErrorMessage('La geolocalización no está disponible en tu navegador');
    }
  }

  function handleConfirmClick() {
    if (!address || !selectedLocation || !district) {
      setErrorMessage('Todos los campos son obligatorios');
      return;
    }

    setErrorMessage('');
    setIsModalOpen(true);
  }

  async function handleSave() {
    setIsModalOpen(false);

    if (selectedLocation && address && district) {
      try {
        const newAddress = {
          address: address,
          district: district,
          latitude: selectedLocation.lat,
          longitude: selectedLocation.lng,
        };

        const token = localStorage.getItem('token');
        await axios.put(`${process.env.REACT_APP_API_URL}/api/user/${userId}`, {
          addresses: [newAddress],
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        setActiveComponent('Recojo');
      } catch (error) {
        console.error('Error saving location:', error);
        setErrorMessage('Error al guardar la ubicación');
      }
    }
  }

  function handleCancel() {
    setIsModalOpen(false);
  }

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setActiveComponent('Recojo')}
              className="flex items-center gap-2 text-slate-700 hover:text-[#B1336E] transition-colors duration-200 font-montserrat"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm sm:text-base">Volver</span>
            </button>
            <h1 className="text-lg sm:text-xl font-extrabold text-[#B1336E] font-montserrat">
              Seleccionar Ubicación
            </h1>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="relative flex-1">
        <div ref={mapContainerRef} className="w-full h-full"></div>

        {/* Initial Alert Overlay */}
        {showInitialAlert && (
          <div className="absolute top-4 left-4 right-4 bg-[#B1336E] text-white rounded-xl p-4 shadow-lg z-10 animate-pulse">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-semibold font-montserrat">
                Toca el mapa para seleccionar tu ubicación de recojo
              </p>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="absolute top-4 left-4 right-4 z-10">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Busca tu dirección..."
                className="w-full pl-12 pr-4 py-3 text-sm font-montserrat focus:outline-none"
              />
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="border-t border-slate-200 max-h-60 overflow-y-auto">
                {searchResults.map((result) => (
                  <button
                    key={result.place_id}
                    onClick={() => handleResultClick(result)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0"
                  >
                    <p className="text-sm text-slate-900 font-montserrat">
                      {result.description}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Current Location Button */}
        <button
          onClick={handleGetCurrentLocation}
          className="absolute bottom-4 right-4 w-12 h-12 bg-white hover:bg-slate-50 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 z-10 border border-slate-200"
          aria-label="Usar mi ubicación actual"
        >
          <Navigation className="w-5 h-5 text-[#B1336E]" />
        </button>
      </div>

      {/* Fixed Footer - Form */}
      <div className="flex-shrink-0 bg-white border-t border-slate-200 shadow-lg z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {/* Address and District Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1 font-montserrat">
                  Dirección
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Ingresa dirección de recojo completa"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] text-sm font-montserrat"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1 font-montserrat">
                  Distrito
                </label>
                <DistrictSelect district={district} setDistrict={setDistrict} />
              </div>
            </div>

            {/* Error Message */}
            {errorMessage && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-700 font-montserrat">{errorMessage}</p>
              </div>
            )}

            {/* Save Button */}
            <button
              onClick={handleConfirmClick}
              className="w-full bg-[#67A43C] hover:bg-[#67A43C]/90 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-sm font-montserrat flex items-center justify-center gap-2"
            >
              <MapPin className="w-5 h-5" />
              <span>Guardar Ubicación</span>
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-start gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#E6B43C]/10 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-[#E6B43C]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
                  Confirmar Ubicación
                </h3>
                <p className="text-sm text-slate-600 font-montserrat">
                  ¿Está seguro de que la ubicación y dirección ingresada son correctas?
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleCancel}
                className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold py-2.5 px-4 rounded-xl transition-colors duration-200 font-montserrat"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="flex-1 bg-[#B1336E] hover:bg-[#B1336E]/90 text-white font-semibold py-2.5 px-4 rounded-xl transition-colors duration-200 font-montserrat"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MapSelection;
